// MyPermissionsCard — read-only view of caller's own role assignments +
// effective permission union, embedded in /dashboard/settings.
//
// Reads `/v1/me/roles` (any logged-in user can call). Renders per-role
// row with name + scope + permission count, and a domain-grouped union
// preview at the bottom. Mirrors UserRolesTab's PermissionPreview but
// scoped to the caller and not editable.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, RefreshCw, ServerCrash } from "lucide-react";
import EmptyState from "./EmptyState.js";
import Skeleton from "./Skeleton.js";
import { getMyRoles, friendlySettingsError, type MyRoles, type MyRoleAssignment } from "../lib/settingsApi.js";
import { PERMISSION_CATALOG, DOMAIN_LABELS } from "../lib/permissionCatalog.js";

type State =
  | { status: "loading" }
  | { status: "ready"; data: MyRoles }
  | { status: "error"; message: string };

export default function MyPermissionsCard(): React.ReactElement {
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await getMyRoles();
      setState({ status: "ready", data });
    } catch (err) {
      setState({ status: "error", message: friendlySettingsError(err) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-bg-raised border border-border">
          <ShieldCheck className="h-4 w-4 text-accent" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-text-primary">我的角色與權限</h2>
          <p className="mt-0.5 text-xs text-text-secondary leading-snug">
            您目前在此單位被指派的角色，以及這些角色帶來的有效權限預覽。
            如需調整，請聯絡單位管理員。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="btn-ghost text-xs"
          disabled={state.status === "loading"}
          aria-label="重新整理"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${state.status === "loading" ? "animate-spin" : ""}`} />
        </button>
      </div>

      {state.status === "loading" && <Skeleton className="h-24 w-full" />}

      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="無法載入您的角色"
          description={state.message}
        />
      )}

      {state.status === "ready" && (
        <ReadyView data={state.data} />
      )}
    </div>
  );
}

function ReadyView({ data }: { data: MyRoles }): React.ReactElement {
  const totalPerms = data.permissions_union.length;
  return (
    <div className="space-y-4">
      {data.assignments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-bg-base p-4 text-2xs text-text-muted text-center">
          您目前在此單位沒有任何角色指派。若這是錯誤狀態，請聯絡單位管理員。
        </div>
      ) : (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
            已指派角色（{data.assignments.length}）
          </h3>
          <div className="rounded-lg border border-border divide-y divide-border">
            {data.assignments.map((a) => (
              <RoleRow key={a.id} a={a} />
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
          有效權限預覽（{totalPerms} 個權限）
        </h3>
        <PermissionPreview permissions={data.permissions_union} />
        <p className="mt-2 text-2xs text-text-muted">
          ※ 預覽以角色內定義的權限聯集計算；範圍限定（scope）效果由 sub-API 在資料層套用。
        </p>
      </section>
    </div>
  );
}

function RoleRow({ a }: { a: MyRoleAssignment }): React.ReactElement {
  const scope = scopeLabel(a.scope_json);
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-text-primary truncate">
          {a.role_name}
          {a.role_is_system && (
            <span className="ml-2 text-2xs text-text-muted">（系統預設）</span>
          )}
        </div>
        <div className="text-2xs text-text-muted">
          {scope} · {a.permissions.length} 個權限
        </div>
      </div>
    </div>
  );
}

function PermissionPreview({ permissions }: { permissions: string[] }): React.ReactElement {
  const byDomain = useMemo(() => {
    const totalByDomain = new Map<string, number>();
    const selByDomain = new Map<string, number>();
    const have = new Set(permissions);
    for (const p of PERMISSION_CATALOG) {
      totalByDomain.set(p.domain, (totalByDomain.get(p.domain) ?? 0) + 1);
      if (have.has(p.id)) selByDomain.set(p.domain, (selByDomain.get(p.domain) ?? 0) + 1);
    }
    // Preserve catalog domain ordering.
    const order: string[] = [];
    const seen = new Set<string>();
    for (const p of PERMISSION_CATALOG) {
      if (!seen.has(p.domain)) { seen.add(p.domain); order.push(p.domain); }
    }
    return order
      .map((d) => ({ domain: d, selected: selByDomain.get(d) ?? 0, total: totalByDomain.get(d) ?? 0 }))
      .filter((x) => x.selected > 0);
  }, [permissions]);

  if (byDomain.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-bg-base p-3 text-2xs text-text-muted text-center">
        沒有任何有效權限
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-bg-base p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
      {byDomain.map((d) => (
        <div key={d.domain} className="flex items-center justify-between text-2xs px-2 py-1 rounded bg-bg-surface">
          <span className="text-text-primary truncate">{DOMAIN_LABELS[d.domain] ?? d.domain}</span>
          <span className={[
            "font-mono",
            d.selected === d.total ? "text-accent" : "text-text-secondary",
          ].join(" ")}>
            {d.selected} / {d.total}
          </span>
        </div>
      ))}
    </div>
  );
}

function scopeLabel(scope: MyRoleAssignment["scope_json"]): string {
  if (!scope) return "整個單位（tenant-wide）";
  const parts: string[] = [];
  if (scope.sites?.length) parts.push(`案場 ${scope.sites.length} 個`);
  if (scope.buildings?.length) parts.push(`樓棟 ${scope.buildings.length} 個`);
  if (scope.clients?.length) parts.push(`客戶 ${scope.clients.length} 個`);
  return parts.length === 0 ? "整個單位（tenant-wide）" : `限定：${parts.join("、")}`;
}
